import React, { useEffect, useState } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid'
import { Checkbox } from 'msp-components'
import { AmountAttributeIndicator } from '@/components/molecules/AmountAttributeIndicator'
import { getBaseCategory, getSubcategories } from '../index'
import * as T from '@/pages/product-list/ProductList.types'
import FilterCount from './FilterCount'
import { isAmountAttribute } from './Filters.utils'
import { FilterLabel } from './FilterLabel'
import { ALLOWED_SUBCATEGORIES } from './Filters'

const FilterListbox = ({
  className = '',
  filter,
  mobile,
  selectedFilters,
  toggleFilter
}: T.FilterListboxProps) => {
  const [selectedRefinements, setSelectedRefinements] = useState<
    T.RefinementValue[]
  >([])
  let values = []
  const amountAttribute = isAmountAttribute(filter.attributeId)

  if (amountAttribute) {
    values = filter.values?.sort(
      (a, b) => parseInt(a.value) - parseInt(b.value)
    )
  } else {
    const subCategories = getSubcategories(filter)
    if (filter.attributeId === 'cgid' && subCategories?.length > 0) {
      values = subCategories
    } else {
      values = filter.values
    }
  }

  function isSelected(value: string): boolean {
    return selectedRefinements.find((element) => element.value === value)
      ? true
      : false
  }

  function handleSelect(value: string, isCategoryFilter?: boolean) {
    const baseCategory = getBaseCategory(filter)

    if (!isSelected(value)) {
      const selectedRefinement = values?.find(
        (element) => element.value === value
      )
      if (selectedRefinement) {
        const selectedRefinementsUpdated = [
          ...selectedRefinements,
          selectedRefinement
        ]
        setSelectedRefinements(selectedRefinementsUpdated)
        toggleFilter(
          value,
          filter?.attributeId,
          false,
          true,
          isCategoryFilter,
          baseCategory?.value
        )
      }
    } else {
      handleDeselect(value)
      toggleFilter(
        value,
        filter?.attributeId,
        true,
        true,
        isCategoryFilter,
        baseCategory?.value
      )
    }
  }

  function handleDeselect(value: string) {
    const selectedRefinementsUpdated = selectedRefinements.filter(
      (element) => element.value !== value
    )
    setSelectedRefinements(selectedRefinementsUpdated)
  }

  useEffect(() => {
    let selected = []

    if (selectedFilters) {
      const value = filter?.values?.[0]

      if (ALLOWED_SUBCATEGORIES.includes(value?.value)) {
        if (value?.values?.length > 0) {
          selected = value?.values?.filter((refinement) =>
            selectedFilters.includes(refinement.value)
          )
        }
      } else {
        selected = filter?.values?.filter((refinement) =>
          selectedFilters.includes(refinement.value)
        )
      }

      setSelectedRefinements(selected)
    }
  }, [selectedFilters])

  return (
    <div className={className}>
      <Listbox
        as='div'
        className='text-14 font-Roboto tracking-0 min-w-min'
        value={selectedRefinements.map((element) => element.value)}
        onChange={(value) =>
          handleSelect(value.toString(), filter?.attributeId === 'cgid')
        }
        key={filter.attributeId}
      >
        {({ open }) => (
          <>
            <div className='relative'>
              <Listbox.Button
                className={`relative flex h-12 w-full items-center justify-between border px-6 py-4 text-left focus:outline-none ${
                  open
                    ? 'border-machine2-75 rounded-b-none rounded-t-sm border-b-0 bg-black'
                    : 'border-machine2-75 rounded bg-white'
                }`}
              >
                <Listbox.Label
                  className={`flex cursor-pointer flex-nowrap space-y-1 truncate whitespace-nowrap ${
                    open ? 'text-white' : 'text-black'
                  } `}
                >
                  {filter?.label}
                  <FilterCount
                    className={open ? 'bg-white text-black' : ''}
                    count={selectedRefinements.length}
                  />
                </Listbox.Label>
                <span
                  className={`pointer-events-none inset-y-0 right-0 flex items-center`}
                >
                  {open ? (
                    <ChevronUpIcon
                      className='h-5 w-5'
                      aria-hidden='true'
                      fill='white'
                    />
                  ) : (
                    <ChevronDownIcon className='h-5 w-5' aria-hidden='true' />
                  )}
                </span>
              </Listbox.Button>
              <Transition
                unmount={false}
                leave='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <Listbox.Options
                  static
                  className={`border-machine1-100 font-Roboto overflow-auto rounded-b-sm border bg-white focus:outline-none ${
                    !mobile && 'absolute z-[1] w-full'
                  }`}
                >
                  {values?.map((refinement) => {
                    const selected = isSelected(refinement.value)
                    return (
                      <Listbox.Option
                        key={refinement.value}
                        value={refinement.value}
                        className={'relative h-10 cursor-pointer select-none'}
                      >
                        {({ active }) => (
                          <div
                            className={`${
                              active && 'bg-machine2-25'
                            } flex h-full items-center truncate px-6`}
                          >
                            <Checkbox
                              className={`font-Roboto pointer-events-none ${
                                selected ? 'font-bold' : ''
                              }`}
                              value={selected}
                              label={
                                <FilterLabel
                                  attributeID={filter.attributeId}
                                  label={refinement.label}
                                />
                              }
                              name={`${filter?.attributeId}_${refinement.value}`}
                              readOnly
                            />
                            {amountAttribute && (
                              <AmountAttributeIndicator
                                className={'space-x-1 fill-black stroke-black'}
                                quantity={Number(refinement.value) || 0}
                                height={'15px'}
                              />
                            )}
                            <span className='text-grayRegular tracking-0 ml-2'>{`(${refinement.hitCount})`}</span>
                          </div>
                        )}
                      </Listbox.Option>
                    )
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}

export default FilterListbox
